import {
    createDataActionFactory,
    HubPersona,
    Feature,
    isConsumer,
    allPeopleFolderId,
    allListsFolderId,
} from '../../../shared';
import type { AppDataState } from '../../../appstate';
import { getSearchUserFolders, getSearchUnifiedGroups } from '../../datalayer/getSearchResults';
import type { Dispatch } from 'redux';
import { QueryData, QuerySource } from '../../models/query';
import { searchAllQueriesExecutedAction } from '../user/searchActions';
import { searchFederatedSources } from '../../datalayer/getFederatedSearchResults';

export const getFederatedBackfillCompletedAction = createDataActionFactory<
    { results: HubPersona[] },
    {}
>('GetFederatedBackfillCompletedAction', Feature.Search);

export const getFederatedSearchResultsCompletedAction = createDataActionFactory<
    {
        queryText: string;
        hubPersonas: HubPersona[];
    },
    { queryText: string }
>('GetFederatedSearchResultsCompletedAction', Feature.Search);

export const getSearchResultsCompletedAction = createDataActionFactory<
    {
        searchedFolder: string;
        searchQuery: string;
        searchResult: HubPersona[];
        isRecipientCache: boolean;
    },
    { searchQuery: string; error: Error }
>('GetSearchResultsCompletedAction', Feature.Search);

const searchUserFolders = (queryData: QueryData): Promise<HubPersona[]> => {
    return getSearchUserFolders(queryData);
};

const searchUnifiedGroups = (queryData: QueryData): Promise<HubPersona[]> => {
    return getSearchUnifiedGroups(queryData);
};

const dispatchSearchActions = (dispatch: Dispatch) => (
    queryData: QueryData,
    hubPersonaList: Promise<HubPersona[]>,
    isRecipientCache: boolean = false
): Promise<void> => {
    return hubPersonaList.then(
        personas => {
            dispatch(
                getSearchResultsCompletedAction.success({
                    searchedFolder: queryData.folderId,
                    searchQuery: queryData.currentQueryString,
                    searchResult: personas,
                    isRecipientCache: isRecipientCache,
                })
            );
        },
        error => {
            dispatch(
                getSearchResultsCompletedAction.error({
                    searchQuery: queryData.currentQueryString,
                    error,
                })
            );
        }
    );
};

export const getFederatedSearchResults = (dispatch: Dispatch) => (queryData: QueryData): void => {
    const queryText = queryData.currentQueryString;
    const federatedSearchPromise = searchFederatedSources({
        ...queryData,
        source: QuerySource.LegacyFederatedSearch,
    });

    const directoryBackfillQuery = { ...queryData, source: QuerySource.Directory };
    const directoryBackfillPromise = searchUserFolders(directoryBackfillQuery).then(results =>
        dispatch(getFederatedBackfillCompletedAction.success({ results }))
    );

    if (!isConsumer()) {
        searchUnifiedGroups(directoryBackfillQuery).then(results =>
            dispatch(getFederatedBackfillCompletedAction.success({ results }))
        );
    }

    directoryBackfillPromise
        .then(_ => federatedSearchPromise)
        .then(hubPersonas =>
            dispatch(getFederatedSearchResultsCompletedAction.success({ queryText, hubPersonas }))
        )
        .catch(error => dispatch(getFederatedSearchResultsCompletedAction.error({ queryText })));
};

export const getSearchResults = (dispatch: Dispatch) => (
    queryData: QueryData,
    appStateData: AppDataState
): void => {
    const queryPromiseList: Promise<void>[] = [];

    if (queryData.source === QuerySource.Groups) {
        // Search unified groups.
        if (!isConsumer()) {
            queryPromiseList.push(
                dispatchSearchActions(dispatch)(queryData, searchUnifiedGroups(queryData))
            );
        }
    } else {
        switch (queryData.folderId) {
            case allPeopleFolderId:
            case allListsFolderId:
                // Include Directory for Persons and Distribution lists
                const directoryQuery = {
                    ...queryData,
                    source: QuerySource.Directory,
                };
                queryPromiseList.push(
                    dispatchSearchActions(dispatch)(
                        directoryQuery,
                        searchUserFolders(directoryQuery)
                    )
                );
                break;
            default:
                queryPromiseList.push(
                    dispatchSearchActions(dispatch)(queryData, searchUserFolders(queryData))
                );
                break;
        }
    }

    Promise.all(queryPromiseList).then(
        () => {
            dispatch(
                searchAllQueriesExecutedAction.success({
                    currentQuery: queryData.currentQueryString,
                })
            );
        },
        (error: Error) => dispatch(searchAllQueriesExecutedAction.error(error))
    );
};
export type { DataActionFactory } from '../../../shared';
export type { Action } from '../../../shared';
